<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useVoice } from '@/composables/useVoice'
import Button from 'primevue/button'

const emit = defineEmits<{
  (e: 'intent', intent: string, entities: Record<string, string>): void
  (e: 'transcript', text: string): void
  (e: 'error', error: string): void
}>()

const {
  isListening,
  isProcessing,
  transcript,
  error,
  startListening,
  stopListening,
  isSupported,
} = useVoice()

const showTranscript = ref(false)

const buttonClass = computed(() => {
  if (isProcessing.value) return 'voice-indicator processing'
  if (isListening.value) return 'voice-indicator listening'
  return 'voice-indicator'
})

const buttonIcon = computed(() => {
  if (isProcessing.value) return 'pi pi-spin pi-spinner'
  if (isListening.value) return 'pi pi-stop'
  return 'pi pi-microphone'
})

async function toggleListening() {
  if (isListening.value) {
    const result = await stopListening()
    if (result) {
      emit('transcript', result.transcript)
      if (result.intent) {
        emit('intent', result.intent, result.entities || {})
      }
    }
  } else {
    showTranscript.value = true
    startListening()
  }
}

// Cleanup on unmount
onUnmounted(() => {
  // Nothing to clean up currently
})
</script>

<template>
  <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center">
    <!-- Transcript display -->
    <Transition
      enter-active-class="transition-all duration-200"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="showTranscript && (transcript || isListening)"
        class="mb-4 px-6 py-3 bg-white dark:bg-gray-800 rounded-2xl shadow-lg max-w-sm"
      >
        <!-- Sound wave when listening -->
        <div v-if="isListening && !transcript" class="sound-wave">
          <span class="sound-wave-bar" />
          <span class="sound-wave-bar" />
          <span class="sound-wave-bar" />
          <span class="sound-wave-bar" />
          <span class="sound-wave-bar" />
        </div>

        <!-- Transcript text -->
        <p v-else-if="transcript" class="text-gray-700 dark:text-gray-300 text-center">
          {{ transcript }}
        </p>

        <!-- Processing indicator -->
        <div v-if="isProcessing" class="flex items-center justify-center gap-2 mt-2">
          <i class="pi pi-spin pi-spinner text-primary-500" />
          <span class="text-sm text-gray-500">Analyse en cours...</span>
        </div>
      </div>
    </Transition>

    <!-- Error message -->
    <Transition
      enter-active-class="transition-all duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="error"
        class="mb-4 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl text-sm"
      >
        {{ error }}
      </div>
    </Transition>

    <!-- Voice button -->
    <button
      v-if="isSupported"
      :class="buttonClass"
      @click="toggleListening"
      :disabled="isProcessing"
    >
      <i :class="buttonIcon" class="text-2xl" />
    </button>

    <!-- Not supported message -->
    <div
      v-else
      class="px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-xl text-sm"
    >
      <i class="pi pi-exclamation-triangle mr-2" />
      Reconnaissance vocale non supportée
    </div>

    <!-- Quick actions when not listening -->
    <Transition
      enter-active-class="transition-all duration-300 delay-150"
      enter-from-class="opacity-0 scale-75"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-200"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-75"
    >
      <div
        v-if="!isListening && !isProcessing && isSupported"
        class="mt-4 flex gap-2"
      >
        <Button
          icon="pi pi-utensils"
          rounded
          outlined
          size="small"
          v-tooltip.top="'Enregistrer un repas'"
          @click="emit('intent', 'log_meal', {})"
        />
        <Button
          icon="pi pi-heart"
          rounded
          outlined
          size="small"
          v-tooltip.top="'Glycémie'"
          @click="emit('intent', 'check_glucose', {})"
        />
        <Button
          icon="pi pi-bolt"
          rounded
          outlined
          size="small"
          v-tooltip.top="'Activité'"
          @click="emit('intent', 'log_activity', {})"
        />
        <Button
          icon="pi pi-question-circle"
          rounded
          outlined
          size="small"
          v-tooltip.top="'Aide'"
          @click="emit('intent', 'help', {})"
        />
      </div>
    </Transition>
  </div>
</template>
